package pl.nickers.match3.ecs

/**
 * A strategy that knows how to detect one specific kind of match pattern.
 *
 * Each strategy is stateless and operates purely on a mutable grid snapshot
 * (a 2-D array of entity IDs, -1 for empty). Implementations must not modify
 * the world; grid mutation (marking matched cells as -1) is the caller's job.
 *
 * Strategies are meant to be invoked in priority order by [findMatchGroups].
 * After a strategy returns a [MatchGroup], the caller clears those entity IDs
 * from the grid and restarts from the highest-priority strategy, guaranteeing
 * that the same entity never contributes to two different [MatchGroup]s and
 * that higher-priority patterns always take precedence.
 */
fun interface MatchStrategy {
    /**
     * Return the first [MatchGroup] found on [grid], or null if this strategy
     * cannot find any match.
     *
     * @param grid      mutable grid snapshot; -1 means the cell is empty / already claimed
     * @param typeMapper mapper to read [JellyTypeComponent] from entity IDs
     * @param width     number of columns in the grid
     * @param height    number of rows in the grid
     */
    fun findFirst(
        grid: Array<IntArray>,
        typeMapper: ComponentMapper<JellyTypeComponent>,
        width: Int,
        height: Int,
    ): MatchGroup?
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Scans left-to-right across every row and returns the first run whose length
 * satisfies [minLen] ≤ len ≤ [maxLen]. Returns the entity-ID list and length, or null.
 */
private fun firstHorizontalRun(
    grid: Array<IntArray>,
    typeMapper: ComponentMapper<JellyTypeComponent>,
    width: Int,
    height: Int,
    minLen: Int,
    maxLen: Int = Int.MAX_VALUE,
): Pair<List<Int>, Int>? {
    for (r in 0 until height) {
        var c = 0
        while (c < width) {
            val eid = grid[r][c]
            if (eid == -1) { c++; continue }
            val type = typeMapper[eid]!!.type
            var len = 1
            while (c + len < width) {
                val nxt = grid[r][c + len]
                if (nxt == -1 || typeMapper[nxt]!!.type != type) break
                len++
            }
            if (len in minLen..maxLen) {
                return (0 until len).map { grid[r][c + it] } to len
            }
            c += len
        }
    }
    return null
}

/**
 * Scans top-to-bottom across every column and returns the first run whose length
 * satisfies [minLen] ≤ len ≤ [maxLen]. Returns the entity-ID list and length, or null.
 */
private fun firstVerticalRun(
    grid: Array<IntArray>,
    typeMapper: ComponentMapper<JellyTypeComponent>,
    width: Int,
    height: Int,
    minLen: Int,
    maxLen: Int = Int.MAX_VALUE,
): Pair<List<Int>, Int>? {
    for (col in 0 until width) {
        var r = 0
        while (r < height) {
            val eid = grid[r][col]
            if (eid == -1) { r++; continue }
            val type = typeMapper[eid]!!.type
            var len = 1
            while (r + len < height) {
                val nxt = grid[r + len][col]
                if (nxt == -1 || typeMapper[nxt]!!.type != type) break
                len++
            }
            if (len in minLen..maxLen) {
                return (0 until len).map { grid[r + it][col] } to len
            }
            r += len
        }
    }
    return null
}

// ---------------------------------------------------------------------------
// Concrete strategies (stateless singletons)
// ---------------------------------------------------------------------------

/**
 * Matches the first horizontal or vertical run of **5 or more** identical jellies,
 * producing a [PieceIds.COLOURFUL_SWIRL] at the centre of the run.
 */
object ColourfulSwirlStrategy : MatchStrategy {
    override fun findFirst(
        grid: Array<IntArray>,
        typeMapper: ComponentMapper<JellyTypeComponent>,
        width: Int,
        height: Int,
    ): MatchGroup? {
        val (entities, len) =
            firstHorizontalRun(grid, typeMapper, width, height, minLen = 5)
                ?: firstVerticalRun(grid, typeMapper, width, height, minLen = 5)
                ?: return null
        return MatchGroup(
            entities = entities,
            seedIndex = len / 2,
            seedPieceId = PieceIds.COLOURFUL_SWIRL,
            priority = MatchPriority.COLOURFUL_SWIRL,
        )
    }
}

/**
 * Matches the first horizontal or vertical run of **exactly 4** identical jellies,
 * producing a laser at the centre of the run. Horizontal run → vertical laser
 * (sweeps the column), vertical run → horizontal laser (sweeps the row).
 */
object LaserStrategy : MatchStrategy {
    override fun findFirst(
        grid: Array<IntArray>,
        typeMapper: ComponentMapper<JellyTypeComponent>,
        width: Int,
        height: Int,
    ): MatchGroup? {
        firstHorizontalRun(grid, typeMapper, width, height, minLen = 4, maxLen = 4)?.let { (entities, len) ->
            return MatchGroup(
                entities = entities,
                seedIndex = len / 2,
                seedPieceId = PieceIds.LASER_VERTICAL,
                priority = MatchPriority.LASER,
            )
        }
        firstVerticalRun(grid, typeMapper, width, height, minLen = 4, maxLen = 4)?.let { (entities, len) ->
            return MatchGroup(
                entities = entities,
                seedIndex = len / 2,
                seedPieceId = PieceIds.LASER_HORIZONTAL,
                priority = MatchPriority.LASER,
            )
        }
        return null
    }
}

/**
 * Matches the first **2×2 square** of identical jellies (scanning top-left to
 * bottom-right), producing a [PieceIds.BOMB] with the top-left cell as seed.
 */
object Bomb2x2Strategy : MatchStrategy {
    override fun findFirst(
        grid: Array<IntArray>,
        typeMapper: ComponentMapper<JellyTypeComponent>,
        width: Int,
        height: Int,
    ): MatchGroup? {
        for (r in 0 until height - 1) {
            for (c in 0 until width - 1) {
                val e00 = grid[r][c];         if (e00 == -1) continue
                val e01 = grid[r][c + 1];     if (e01 == -1) continue
                val e10 = grid[r + 1][c];     if (e10 == -1) continue
                val e11 = grid[r + 1][c + 1]; if (e11 == -1) continue
                val t = typeMapper[e00]!!.type
                if (typeMapper[e01]!!.type != t ||
                    typeMapper[e10]!!.type != t ||
                    typeMapper[e11]!!.type != t) continue
                return MatchGroup(
                    entities = listOf(e00, e01, e10, e11),
                    seedIndex = 0,
                    seedPieceId = PieceIds.BOMB,
                    priority = MatchPriority.BOMB_2X2,
                )
            }
        }
        return null
    }
}

/**
 * Matches the first pair of perpendicular **exactly-3** runs of identical jellies that share
 * exactly one junction entity, forming a T, L, or X shape (5 entities total).
 *
 * Produces a [PieceIds.CROSS_BOMB] with the junction as seed.  Higher-priority strategies
 * ([ColourfulSwirlStrategy], [LaserStrategy]) consume 4+ and 5+ runs first, so by the time
 * this strategy runs all remaining runs are at most 3 long — making the `== 3` guard
 * conservative but safe.
 */
object CrossBombStrategy : MatchStrategy {
    override fun findFirst(
        grid: Array<IntArray>,
        typeMapper: ComponentMapper<JellyTypeComponent>,
        width: Int,
        height: Int,
    ): MatchGroup? {
        // Collect all horizontal runs of exactly 3
        val hRuns = mutableListOf<List<Int>>()
        for (r in 0 until height) {
            var c = 0
            while (c < width) {
                val eid = grid[r][c]
                if (eid == -1) { c++; continue }
                val type = typeMapper[eid]!!.type
                var len = 1
                while (c + len < width) {
                    val nxt = grid[r][c + len]
                    if (nxt == -1 || typeMapper[nxt]!!.type != type) break
                    len++
                }
                if (len == 3) hRuns.add(listOf(grid[r][c], grid[r][c + 1], grid[r][c + 2]))
                c += len
            }
        }
        // Collect all vertical runs of exactly 3
        val vRuns = mutableListOf<List<Int>>()
        for (col in 0 until width) {
            var r = 0
            while (r < height) {
                val eid = grid[r][col]
                if (eid == -1) { r++; continue }
                val type = typeMapper[eid]!!.type
                var len = 1
                while (r + len < height) {
                    val nxt = grid[r + len][col]
                    if (nxt == -1 || typeMapper[nxt]!!.type != type) break
                    len++
                }
                if (len == 3) vRuns.add(listOf(grid[r][col], grid[r + 1][col], grid[r + 2][col]))
                r += len
            }
        }
        // Find first intersecting (h, v) pair sharing exactly one entity
        for (hRun in hRuns) {
            val hSet = hRun.toSet()
            for (vRun in vRuns) {
                val shared = hSet intersect vRun.toSet()
                if (shared.size == 1) {
                    val junction = shared.first()
                    val allEntities = (hRun + vRun).distinct()
                    return MatchGroup(
                        entities = allEntities,
                        seedIndex = allEntities.indexOf(junction),
                        seedPieceId = PieceIds.CROSS_BOMB,
                        priority = MatchPriority.CROSS_BOMB,
                    )
                }
            }
        }
        return null
    }
}

/**
 * Matches the first horizontal or vertical run of **exactly 3** identical jellies.
 * Produces a delete-only [MatchGroup] (no special tile seeded).
 */
object NormalMatchStrategy : MatchStrategy {
    override fun findFirst(
        grid: Array<IntArray>,
        typeMapper: ComponentMapper<JellyTypeComponent>,
        width: Int,
        height: Int,
    ): MatchGroup? {
        val (entities, _) =
            firstHorizontalRun(grid, typeMapper, width, height, minLen = 3, maxLen = 3)
                ?: firstVerticalRun(grid, typeMapper, width, height, minLen = 3, maxLen = 3)
                ?: return null
        return MatchGroup(
            entities = entities,
            seedIndex = -1,
            seedPieceId = null,
            priority = MatchPriority.NORMAL,
        )
    }
}

/**
 * The canonical strategy list ordered from **highest** to **lowest** priority.
 * [findMatchGroups] iterates this list and restarts from the top after every
 * successful match, so higher-priority patterns always take precedence over
 * lower-priority ones that would otherwise consume the same entities.
 */
val defaultMatchStrategies: List<MatchStrategy> = listOf(
    ColourfulSwirlStrategy,
    LaserStrategy,
    CrossBombStrategy,
    Bomb2x2Strategy,
    NormalMatchStrategy,
)
